import React, { Component } from "react";
import {
  Modal,
  Table,
  Tag,
  Button,
  Tabs,
  Badge,
  Tooltip,
  notification,
  Spin,
  Alert,
} from "antd";
import {
  SyncOutlined,
  PlayCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  CopyOutlined,
} from "@ant-design/icons";
import moment from "moment";
import {
  fetchCronJobRunsAPI,
  triggerCronJobNowAPI,
} from "../../Graphs/Report/cronJobsAPI";

const { TabPane } = Tabs;

export class CronJobRunsModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      runs: [],
      total: 0,
      page: 1,
      pageSize: 15,
      statusFilter: "all",
      isLoading: false,
      isRunningJob: false,
      selectedFailedRun: null,
      isFailureModalOpen: false,
    };
  }

  componentDidMount() {
    this._isMounted = true;
    if (this.props.job) {
      this.loadJobRuns(1);
    }
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.job &&
      (!prevProps.job || prevProps.job.id !== this.props.job.id)
    ) {
      this.setState({ page: 1, statusFilter: "all" }, () => {
        this.loadJobRuns(1);
      });
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  safeSetState = (state, callback) => {
    if (this._isMounted) {
      this.setState(state, callback);
    }
  };

  loadJobRuns = async (page = 1) => {
    const { job, authToken } = this.props;
    if (!job) return;

    try {
      this.safeSetState({ isLoading: true });
      const { statusFilter, pageSize } = this.state;
      const res = await fetchCronJobRunsAPI(authToken, job.id, {
        page,
        limit: pageSize,
        status: statusFilter === "all" ? null : statusFilter,
      });

      if (res && res.status === 200 && res.data) {
        this.safeSetState({
          runs: res.data.runs || [],
          total: res.data.total || 0,
          page: res.data.page || page,
        });
      } else {
        this.safeSetState({ runs: [], total: 0 });
      }
    } catch (err) {
      console.error("[CronJobRunsModal] loadJobRuns error:", err);
      notification.error({
        message: "Failed to load run instances",
        description: err.message || "Network error",
      });
    } finally {
      this.safeSetState({ isLoading: false });
    }
  };

  handleFilterChange = (status) => {
    this.setState({ statusFilter: status, page: 1 }, () => {
      this.loadJobRuns(1);
    });
  };

  handleRunNow = () => {
    const { job, authToken, onJobExecuted } = this.props;
    if (!job) return;

    Modal.confirm({
      title: `Run "${job.name}" Immediately?`,
      icon: <ExclamationCircleOutlined />,
      content:
        "This will trigger immediate on-demand execution in the backend. Are you sure you want to proceed?",
      okText: "Run Now",
      cancelText: "Cancel",
      okButtonProps: { type: "primary" },
      onOk: async () => {
        try {
          this.safeSetState({ isRunningJob: true });
          const res = await triggerCronJobNowAPI(authToken, job.id);
          if (res && res.status === 200) {
            notification.success({
              message: "Job Executed Successfully",
              description: `Job "${job.name}" completed successfully in ${res.data?.durationMs || 0}ms.`,
            });
            this.loadJobRuns(1);
            if (onJobExecuted) onJobExecuted();
          } else {
            notification.error({
              message: "Job Execution Failed",
              description:
                res?.message || res?.error || "Execution encountered an error.",
            });
            this.loadJobRuns(1);
            if (onJobExecuted) onJobExecuted();
          }
        } catch (err) {
          notification.error({
            message: "Execution Request Error",
            description: err.message || "Failed to trigger job.",
          });
          this.loadJobRuns(1);
        } finally {
          this.safeSetState({ isRunningJob: false });
        }
      },
    });
  };

  formatDuration = (durationMs) => {
    if (durationMs === null || durationMs === undefined) return "-";
    if (durationMs < 1000) return `${durationMs}ms`;
    return `${(durationMs / 1000).toFixed(2)}s`;
  };

  getStatusTag = (status) => {
    switch (status) {
      case "completed":
        return <Tag color="success" icon={<CheckCircleOutlined />}>COMPLETED</Tag>;
      case "failed":
        return <Tag color="error" icon={<CloseCircleOutlined />}>FAILED</Tag>;
      case "running":
        return <Tag color="processing" icon={<SyncOutlined spin />}>RUNNING</Tag>;
      case "partial":
        return <Tag color="warning" icon={<ClockCircleOutlined />}>PARTIAL</Tag>;
      default:
        return <Tag>{status || "UNKNOWN"}</Tag>;
    }
  };

  openFailureDetails = (record) => {
    this.safeSetState({
      selectedFailedRun: record,
      isFailureModalOpen: true,
    });
  };

  copyStackTrace = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(
      () => {
        notification.success({ message: "Stack trace copied to clipboard" });
      },
      () => {
        notification.error({ message: "Failed to copy to clipboard" });
      }
    );
  };

  renderFailureModal = () => {
    const { selectedFailedRun, isFailureModalOpen } = this.state;
    if (!selectedFailedRun) return null;

    return (
      <Modal
        title={
          <span style={{ color: "#cf1322" }}>
            <CloseCircleOutlined style={{ marginRight: 8 }} />
            Failure Reason & Stack Trace
          </span>
        }
        visible={isFailureModalOpen}
        onCancel={() => this.safeSetState({ isFailureModalOpen: false })}
        footer={[
          <Button
            key="copy"
            icon={<CopyOutlined />}
            onClick={() =>
              this.copyStackTrace(
                `${selectedFailedRun.errorMessage}\n\n${selectedFailedRun.errorStack || ""}`
              )
            }
          >
            Copy Error Details
          </Button>,
          <Button
            key="close"
            type="primary"
            onClick={() => this.safeSetState({ isFailureModalOpen: false })}
          >
            Close
          </Button>,
        ]}
        width={750}
        className="failure-details-modal"
      >
        <div className="failure-meta-grid">
          <div className="meta-item">
            <span className="label">Run ID:</span>
            <span className="value" style={{ fontFamily: "monospace" }}>
              {selectedFailedRun.id}
            </span>
          </div>
          <div className="meta-item">
            <span className="label">Triggered By:</span>
            <span className="value">
              {selectedFailedRun.triggerType === "manual"
                ? `Manual (${selectedFailedRun.triggeredBy})`
                : "System Cron"}
            </span>
          </div>
          <div className="meta-item">
            <span className="label">Failed At:</span>
            <span className="value">
              {moment(selectedFailedRun.finishedAt || selectedFailedRun.startedAt).format(
                "YYYY-MM-DD HH:mm:ss"
              )}
            </span>
          </div>
          <div className="meta-item">
            <span className="label">Execution Duration Before Failure:</span>
            <span className="value">
              {this.formatDuration(selectedFailedRun.durationMs)}
            </span>
          </div>
        </div>

        <div className="error-alert-box">
          <div className="error-title">
            <ExclamationCircleOutlined /> Error Message:
          </div>
          <div className="error-message-text">
            {selectedFailedRun.errorMessage || "No specific error message was captured."}
          </div>
        </div>

        {selectedFailedRun.errorStack ? (
          <div className="stack-trace-section">
            <h5>Stack Trace:</h5>
            <pre>{selectedFailedRun.errorStack}</pre>
          </div>
        ) : (
          <Alert
            message="No stack trace available for this failure"
            type="info"
            showIcon
          />
        )}
      </Modal>
    );
  };

  render() {
    const { visible, onClose, job } = this.props;
    const { runs, total, page, pageSize, isLoading, isRunningJob, statusFilter } =
      this.state;

    if (!job) return null;

    const columns = [
      {
        title: "Run ID",
        dataIndex: "id",
        key: "id",
        width: 130,
        render: (id) => (
          <span
            style={{ fontFamily: "monospace", fontSize: 11, color: "#595959" }}
            title={id}
          >
            {id ? `${id.substring(0, 8)}...` : "-"}
          </span>
        ),
      },
      {
        title: "Trigger",
        dataIndex: "triggerType",
        key: "triggerType",
        width: 140,
        render: (type, record) => (
          <div>
            <Tag color={type === "manual" ? "purple" : "blue"}>
              {type === "manual" ? "MANUAL" : "SCHEDULED"}
            </Tag>
            <div style={{ fontSize: 11, color: "#8c8c8c", marginTop: 2 }}>
              {record.triggeredBy}
            </div>
          </div>
        ),
      },
      {
        title: "Started At",
        dataIndex: "startedAt",
        key: "startedAt",
        width: 160,
        render: (time) => (
          <div>
            <div style={{ fontWeight: 500, fontSize: 13 }}>
              {time ? moment(time).format("YYYY-MM-DD HH:mm:ss") : "-"}
            </div>
            <div style={{ fontSize: 11, color: "#8c8c8c" }}>
              {time ? moment(time).fromNow() : ""}
            </div>
          </div>
        ),
      },
      {
        title: "Duration",
        dataIndex: "durationMs",
        key: "durationMs",
        width: 100,
        render: (ms) => (
          <span style={{ fontFamily: "monospace", fontWeight: 500 }}>
            {this.formatDuration(ms)}
          </span>
        ),
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        width: 120,
        render: (status) => this.getStatusTag(status),
      },
      {
        title: "Result / Failure Reason",
        key: "details",
        render: (_, record) => {
          if (record.status === "failed") {
            return (
              <div className="failure-reason-cell">
                <div
                  className="error-snippet"
                  title={record.errorMessage}
                >
                  {record.errorMessage
                    ? record.errorMessage.length > 50
                      ? `${record.errorMessage.substring(0, 50)}...`
                      : record.errorMessage
                    : "Failed with error"}
                </div>
                <Button
                  size="small"
                  type="link"
                  danger
                  icon={<ExclamationCircleOutlined />}
                  style={{ padding: 0, height: "auto" }}
                  onClick={() => this.openFailureDetails(record)}
                >
                  Why it failed?
                </Button>
              </div>
            );
          }

          if (record.resultSummary) {
            const summaryStr =
              typeof record.resultSummary === "string"
                ? record.resultSummary
                : JSON.stringify(record.resultSummary);
            return (
              <span
                style={{
                  fontSize: 12,
                  color: "#595959",
                  fontFamily: "monospace",
                  wordBreak: "break-all",
                }}
              >
                {summaryStr.length > 60
                  ? `${summaryStr.substring(0, 60)}...`
                  : summaryStr}
              </span>
            );
          }

          return <span style={{ color: "#bfbfbf" }}>-</span>;
        },
      },
    ];

    return (
      <>
        <Modal
          title={
            <span>
              <ClockCircleOutlined style={{ marginRight: 8, color: "#1890ff" }} />
              Execution Instances: {job.name}
            </span>
          }
          visible={visible}
          onCancel={onClose}
          footer={[
            <Button key="refresh" icon={<SyncOutlined />} onClick={() => this.loadJobRuns(page)}>
              Refresh
            </Button>,
            <Button key="close" type="primary" onClick={onClose}>
              Close
            </Button>,
          ]}
          width={960}
          className="cron-runs-modal"
        >
          <div className="modal-job-header">
            <div className="job-meta">
              <h4>{job.name}</h4>
              <p>{job.description}</p>
              <div style={{ marginTop: 4 }}>
                <span
                  style={{
                    fontFamily: "monospace",
                    background: "#e6f7ff",
                    padding: "2px 8px",
                    borderRadius: 4,
                    fontSize: 12,
                    color: "#096dd9",
                    marginRight: 8,
                  }}
                >
                  Schedule: {job.cronTime || "Manual"}
                </span>
                <span style={{ fontSize: 12, color: "#8c8c8c" }}>
                  Timezone: {job.timezone}
                </span>
              </div>
              <div className="retention-tag">
                <Tag color="cyan">Data Retained for 30 Days</Tag>
              </div>
            </div>
            <div className="modal-job-actions">
              <Button
                type="primary"
                icon={<PlayCircleOutlined />}
                loading={isRunningJob}
                disabled={job.isRunning || isRunningJob}
                onClick={this.handleRunNow}
              >
                Run Job Now
              </Button>
            </div>
          </div>

          <Tabs
            activeKey={statusFilter}
            onChange={this.handleFilterChange}
            className="runs-filter-tabs"
          >
            <TabPane tab="All Runs" key="all" />
            <TabPane
              tab={
                <span>
                  <CheckCircleOutlined style={{ color: "#52c41a" }} /> Completed
                </span>
              }
              key="completed"
            />
            <TabPane
              tab={
                <span>
                  <CloseCircleOutlined style={{ color: "#ff4d4f" }} /> Failed
                </span>
              }
              key="failed"
            />
            <TabPane
              tab={
                <span>
                  <SyncOutlined spin style={{ color: "#1890ff" }} /> Running
                </span>
              }
              key="running"
            />
          </Tabs>

          <Table
            dataSource={runs}
            columns={columns}
            rowKey="id"
            loading={isLoading}
            size="middle"
            pagination={{
              current: page,
              pageSize,
              total,
              onChange: (newPage) => {
                this.setState({ page: newPage }, () => {
                  this.loadJobRuns(newPage);
                });
              },
              showTotal: (totalCount) => `Total ${totalCount} instances in last 30 days`,
            }}
          />
        </Modal>

        {this.renderFailureModal()}
      </>
    );
  }
}

export default CronJobRunsModal;
